import { useCallback, useMemo } from 'react';
import { useBadges } from '../context/NotificationProvider';
import { notificationsAPI } from '../services/api';
/**
 * useAdminSidebarBadges — thin adapter over NotificationProvider.
 *
 * Previously this hook ran a 45s poller that fanned out to 4 endpoints. That
 * poller (plus useStaffSidebarBadges, plus a bell poller inside DashboardLayout,
 * plus a chat poller) added up to ~6 independent clocks. Now every count comes
 * from the SINGLE /api/badges poll (5s cadence) that NotificationProvider
 * owns — this hook just projects that unified payload into the shape the
 * Sidebar already expects: { <itemId>: { kind: 'count', value: N } }.
 *
 * Mapping (backend badge → sidebar itemId):
 *   verifications → 'admin'
 *   leads         → 'live-leads'
 *   missing_pay   → 'payouts'
 *   chat          → 'team-chat'
 *
 * clearBadge() still exists for the sidebar's "clear-on-view" behaviour; it
 * calls the appropriate optimistic path (ack for derived, mark-all-read for
 * bell-driven) on the provider so the count drops instantly.
 */
const CATEGORY_BY_ITEM_ID = {
  'admin': 'verifications',
  // `leads` counts PIPELINE inquiries, which land on Automations. It used to
  // be pinned to 'live-leads', so a pipeline lead badged the QR capture page
  // - a page that was empty, and that cleared the badge when you visited it.
  'automations': 'leads',
  'live-leads': 'live_leads',
  'payouts': 'missing_pay',
  'team-chat': 'chat',
};
export default function useAdminSidebarBadges(user) {
  const isAdminSide = !!user && (user.role || '') !== 'staff';
  const { badges: raw, ackCategory } = useBadges();
  const badges = useMemo(() => {
    if (!isAdminSide) return {};
    const out = {};
    for (const [itemId, category] of Object.entries(CATEGORY_BY_ITEM_ID)) {
      const value = Number(raw[category] || 0);
      if (value > 0) out[itemId] = { kind: 'count', value };
    }
    return out;
  }, [isAdminSide, raw]);
  const clearBadge = useCallback(async (itemId) => {
    const category = CATEGORY_BY_ITEM_ID[itemId];
    if (!category) return;
    // Optimistic: NotificationProvider handles the "instant zero + revert on
    // failure" contract for us. Verifications/chat/missing-pay use ack;
    // there's no bell path here (bell is handled where the bell UI opens).
    ackCategory(category);
    // Also mark matching notifications as read for the legacy bell view so
    // the list page doesn't show them as unread after acknowledging.
    if (category === 'leads' || category === 'live_leads') {
      try {
        const res = await notificationsAPI.getAll(500, true).catch(() => null);
        const items = res?.notifications || (Array.isArray(res) ? res : []);
        // Both sides share type=new_lead, so filtering on type ALONE would
        // let one page's visit silently clear the other's badge - the same
        // conflation this block exists to undo, moved into the ack path.
        const wantLive = category === 'live_leads';
        const targets = items.filter((n) => (n.type || '') === 'new_lead'
          && ((n.relatedEntityType || '') === 'live_lead') === wantLive);
        await Promise.all(targets.map((n) => notificationsAPI.markAsRead(n.id).catch(() => null)));
      } catch { /* non-fatal */ }
    }
  }, [ackCategory]);
  return { badges, clearBadge };
}
